import {
  CreateUserAvailabilityBulkInput,
  CreateUserAvailabilityBulkResponse,
  CreateUserAvailabilityInput,
  UserAvailabilityTypeEnum,
} from '@/models/gen/graphql';
import { Datetime, Validation, dateWithCurrentTime, dateWithExistingTime, titleCase } from '@/utils';
import { createNotification, handleError } from '@/utils/custom';

import { CreateUserAvailabilityBulkDocument } from '@/api/queries';
import { DATE_FE_FORMAT_FULL } from '@/constants';
import { GraphApiResponse } from '@/api/core';
import Logger from '@/utils/logs';
import { Toast } from '@/models';
import graphApi from '@/api';

const log = Logger.of('CreateUserAvailabilityBulk');

type CreateUserAvailabilityBulkGraphApiResponse = GraphApiResponse<typeof CreateUserAvailabilityBulkDocument>;

const title = 'Create User Availability';

export const createUserAvailabilityValidator = new Validation.Validator({
  'startDate!': (val: string): Validation.Validity => ({
    valid: Validation.isDate(val),
  }),
  'endDate!': (val: string): Validation.Validity => ({
    valid: Validation.isDate(val),
  }),
  'startTime!': (val: string): Validation.Validity => ({
    valid: !!val,
  }),
  'endTime!': (val: string): Validation.Validity => ({
    valid: !!val,
  }),
  'dayOfWeek!': (val: number): Validation.Validity => ({
    valid: Validation.isNumber(val),
  }),
  availabilityType: (val: string): Validation.Validity => ({
    valid: val ? true : val?.length ? false : undefined,
  }),
  type: (val: UserAvailabilityTypeEnum): Validation.Validity => ({
    valid: UserAvailabilityTypeEnum[titleCase(val)] ? true : val?.length ? false : undefined,
  }),
  'repeatUntil!': (val: string): Validation.Validity => ({
    valid: Validation.isDate(val) ? true : val?.length ? false : undefined,
  }),
  reason: (val: string): Validation.Validity => ({
    valid: val ? true : val?.length ? false : undefined,
  }),
  'userId!': (val: string): Validation.Validity => ({
    valid: !!Validation.isValidUUID(val),
  }),
});

const createUserAvailabilityBulkResponseSelector = (res: CreateUserAvailabilityBulkGraphApiResponse): CreateUserAvailabilityBulkResponse =>
  !res.errors ? (res?.createUserAvailabilityBulk as CreateUserAvailabilityBulkResponse) : undefined;

const [runCreateUserAvailabilityBulk] = graphApi(CreateUserAvailabilityBulkDocument, {
  onError: (res: CreateUserAvailabilityBulkGraphApiResponse): void => handleError(res, { notification: { title } }),
});

const createUserAvailabilityBulk = async (availabilities: CreateUserAvailabilityInput[]): Promise<CreateUserAvailabilityBulkResponse> => {
  const values: CreateUserAvailabilityInput[] = availabilities.map(
    (availability: CreateUserAvailabilityInput): CreateUserAvailabilityInput => createUserAvailabilityValidator.create(availability)
  );
  const input: CreateUserAvailabilityBulkInput = { values };
  const res = await runCreateUserAvailabilityBulk({ input });
  const selected = createUserAvailabilityBulkResponseSelector(res);

  if (selected) createNotification('Success', Toast.Type.SUCCESS, title);

  return selected;
};

export const buildAvailabilityPayload = (data: CreateUserAvailabilityInput, dateStr: string): CreateUserAvailabilityInput => {
  const startDate = new Datetime(data.startDate).setDate(dateStr);
  const endDate = new Datetime(data.endDate).setDate(dateStr);
  const start = startDate.setTime(data.startTime);
  let end = endDate.setTime(data.endTime);

  // If start crosses midnight beyond end, add a day
  if (start.asDayjs().isAfter(end.toString())) {
    end = end.add(1, 'day');
  }

  const payload: CreateUserAvailabilityInput = {
    ...data,
    startDate: start.setTime('00:00:00').toString(),
    endDate: end.setTime('00:00:00').toString(),
    startTime: start.setTime(data.startTime).fullTime,
    endTime: end.setTime(data.endTime).fullTime,
    dayOfWeek: startDate.asDayjs().day(), // numeric day of week (0 = Sunday, 6 = Saturday)
  };

  // Ensure repeatUntil is end-of-day if specified
  if (data.repeatUntil) {
    payload.repeatUntil = new Datetime(data.repeatUntil).setTime('23:59:59').toString();
  }
  return payload;
};

export const handleCreateAvailability = async (
  data: CreateUserAvailabilityInput,
  selectedDates: string[]
): Promise<CreateUserAvailabilityBulkResponse> => {
  try {
    // Basic validation
    if (!data?.startTime || !data?.endTime) {
      throw new Error('Please select a start and end time.');
    }
    if (!data?.startDate || !data?.endDate) {
      throw new Error('Missing start and end date.');
    }

    // Check if start/end times match or cross midnight
    const baseStart = new Datetime(data.startDate).setTime(data.startTime);
    let baseEnd = new Datetime(data.startDate).setTime(data.endTime);
    if (baseStart.asDayjs().isSame(baseEnd.toString())) {
      throw new Error('Availability cannot start and stop at the same time.');
    }
    if (baseEnd.isBefore(baseStart.toString())) {
      baseEnd = baseEnd.add(1, 'day');
    }
    data.startDate = baseStart.format(DATE_FE_FORMAT_FULL);
    data.endDate = baseEnd.format(DATE_FE_FORMAT_FULL);

    // If no specific dates, default to [data.startDate]
    const datesToProcess = selectedDates?.length ? selectedDates : [data.startDate];

    // 1) SHIFT PAST DATES TO NEXT WEEK
    //    For each date, if it's before "today" (current time, so past availability), add 1 week.
    const shiftedDates = datesToProcess.map((dateStr) => {
      let dateObj = new Datetime(dateStr);
      const currentDateAndTime = dateWithCurrentTime(new Datetime().toString()).toString();
      const dateObjWithExistingTime = dateWithExistingTime(dateObj.toString(), new Datetime(`${dateStr} ${data?.startTime}`).toString());
      if (dateObjWithExistingTime.isBefore(currentDateAndTime.toString(), 'day')) {
        dateObj = dateObj.add(1, 'week'); // shift exactly 1 week into the future
      }
      return dateObj.toString(); // use the updated date for next steps
    });

    // 2) FILTER BY REPEAT-UNTIL (if specified)
    //    Exclude any date after data.repeatUntil
    let validDates = shiftedDates;
    if (data.repeatUntil) {
      const repeatUntil = new Datetime(data.repeatUntil).setTime('23:59:59');
      validDates = validDates.filter((dateStr) => {
        const dateObj = new Datetime(dateStr);
        return dateObj.asDayjs().isSameOrBefore(repeatUntil.asDayjs());
      });
    }

    // 3) If no valid dates remain, decide what to do
    if (!validDates.length) {
      throw new Error('All selected (or shifted) dates are after the repeatUntil date or were invalid. Nothing to create.');
    }

    // 4) Build payload for each valid date
    const payloads = validDates.map((dateStr) => buildAvailabilityPayload(data, dateStr));

    // 5) Create them in bulk
    return createUserAvailabilityBulk(payloads);
  } catch (err: any) {
    const message = err.message || err;
    log.error('handleCreateAvailability', message).notify({ title: 'Error', message });
    throw err;
  }
};

export default createUserAvailabilityBulk;
